import { useContext, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { AccessTime, ContentCopyOutlined, Edit, PlaceOutlined } from '@mui/icons-material'
import { Button, CircularProgress, DialogActions, DialogContent, Grid, IconButton, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material'
import { Form, Formik } from 'formik'
import { isNumber } from 'lodash'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import AddressForm from '../../../../components/address-form'
import DialogWithTitle from '../../../../components/dialog-with-title'
import ExternalLinkMenuItem from '../../../../components/external-link-menu-item'
import { AppContext, useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import { INSIGHTS_URL, PROTECT_LITE_URL } from '../../../../utils/constants'
import { formatAddress, getFormattedDate, getLocalFormattedDate, getLogo, isValidTimezone, openSensorStore } from '../../../../utils/helpers'
import useClipboard from '../../../../utils/hooks/useClipboard'
import useFolderTheme from '../../../../utils/hooks/useFolderTheme'
import useTemperatures from '../../../../utils/hooks/useTemperatures'
import useSharedStyles from '../../../../utils/shared.styles'
import AccountsContext from '../../context'
import ProtectMobileDialog from '../folder-tab-panel/protect-mobile-dialog'

import useStyles from './index.styles'

const addressSchema = Yup.object().shape({
  address: Yup.string().required('Required'),
  locality: Yup.string().required('Required'),
  region: Yup.string().required('Required'),
  code: Yup.string().required('Required'),
  country: Yup.string().required('Required'),
})

const Sidebar = ({ aboutData, refetch }: { aboutData: IFolderAbout; refetch: () => Promise<void> }) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()
  const auth = useAuth()
  const host = useHost()
  const { enqueueSnackbar } = useSnackbar()
  const { formatTemperature } = useTemperatures()

  const { allTenants, tenantId } = useContext(AppContext)

  const { temperature, relativeHumidity, weatherDescription, timezone, folderAddress } = aboutData

  const { folder } = useContext(AccountsContext)
  const { folderTheme } = useFolderTheme(folder?.id)
  const { clickToCopy } = useClipboard()
  const showStoreLink = folderTheme?.sensorStoreLink === true || folderTheme?.sensorStoreLink === 'true'

  const [protectMobileDialog, setProtectMobileDialog] = useState<boolean>(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const [editAddress, setEditAddress] = useState(false)

  const tenant = allTenants?.find((t) => t.id === Number(tenantId))
  const hasInsights = !!tenant?.fqdns?.insights
  const hasProtectLite = !!tenant?.fqdns?.protect

  const initialAddressValues: AddressFormValues = useMemo(() => folderAddress, [folderAddress])

  const onSubmitAddress = async (values: AddressFormValues) => {
    if (folder) {
      const res = await Api.put(`${host}/api/folders/${folder.id}`, {
        ...folder,
        information: {
          ...folder.information,
          address: {
            ...folder.information.address,
            ...values,
          },
        },
      })
      if (res.ok) {
        enqueueSnackbar('Address updated successfully')
        setEditAddress(false)
        refetch()
      } else {
        enqueueSnackbar('Could not update folder address', { variant: 'error' })
      }
    }
  }

  const logo = getLogo(folderTheme, host)

  const menuOpen = Boolean(menuAnchor)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  return (
    <Grid
      container={true}
      sx={[
        {
          flexDirection: 'column',
          alignSelf: 'start',
          gap: 1,
        },
        classes.informationSideBar,
      ]}
    >
      {folderTheme && folder ? (
        <>
          {logo && <img src={logo} alt="Company Logo" style={classes.companyLogo(theme)} />}

          <Typography variant="body2">{getFormattedDate(new Date().toISOString(), 'EEEE, MMMM d')}</Typography>

          {!(temperature === 0 && relativeHumidity === 0) && (isNumber(temperature) || isNumber(relativeHumidity)) ? (
            <Typography variant="h4" color="primary">
              {isNumber(temperature) ? (
                <>
                  {formatTemperature(temperature, 'integer', 'degF', false)}
                  <sup style={classes.superscript}>°</sup>
                </>
              ) : null}
              {isNumber(temperature) && isNumber(relativeHumidity) ? '/' : null}
              {isNumber(relativeHumidity) ? (
                <>
                  {relativeHumidity}
                  <sup style={classes.superscript}>%</sup>
                </>
              ) : null}
            </Typography>
          ) : null}

          {weatherDescription && (
            <Typography
              variant="body2"
              color="primary"
              sx={{
                marginTop: -1.5,
              }}
            >
              {weatherDescription}
            </Typography>
          )}

          <Typography variant="caption" sx={[sharedClasses.secondaryText, { marginTop: 3 }]}>
            Location Info
          </Typography>

          <Grid
            container={true}
            sx={{
              gap: 2,
            }}
          >
            {timezone && isValidTimezone(timezone) && (
              <Grid
                container={true}
                sx={{
                  gap: 1,
                }}
              >
                <AccessTime color="action" />
                <Typography variant="body1">{getLocalFormattedDate(timezone, 'h:mma')} local time</Typography>
              </Grid>
            )}

            {folderAddress && (
              <Grid
                container={true}
                sx={{
                  gap: 1,
                }}
              >
                <PlaceOutlined color="action" />

                <Grid
                  sx={{
                    flex: 1,
                  }}
                >
                  <Typography variant="body1" className="fs-exclude">
                    {folderAddress.address}
                    {folderAddress.address2 ? `, ${folderAddress.address2}` : ''}
                  </Typography>
                  <Typography variant="body1" className="fs-exclude">
                    {formatAddress(`${folderAddress.locality}, ${folderAddress.region} ${folderAddress.code} ${folderAddress.country}`, folderAddress.country)}
                  </Typography>
                </Grid>

                <Grid>
                  <Tooltip title="Copy" disableInteractive={true}>
                    <IconButton
                      size="small"
                      onClick={() => clickToCopy(formatAddress(folderAddress, folderAddress.country))}
                      aria-label="Copy address to clipboard"
                    >
                      <ContentCopyOutlined fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={() => setEditAddress(true)} aria-label="Edit address">
                    <Edit fontSize="small" color="primary" />
                  </IconButton>

                  <DialogWithTitle title="Location Address" open={editAddress} setOpen={setEditAddress}>
                    <Formik initialValues={initialAddressValues} onSubmit={onSubmitAddress} validationSchema={addressSchema} enableReinitialize={true}>
                      {({ isSubmitting, dirty, isValid }) => (
                        <Form>
                          <DialogContent>
                            <AddressForm fullWidth={true} />
                            <DialogActions>
                              <Button onClick={() => setEditAddress(false)}>Cancel</Button>
                              <Button variant="contained" type="submit" disabled={isSubmitting || !dirty || !isValid}>
                                Save
                              </Button>
                            </DialogActions>
                          </DialogContent>
                        </Form>
                      )}
                    </Formik>
                  </DialogWithTitle>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid container={true} sx={{ flexDirection: 'column', gap: 2, marginTop: 4, marginBottom: 3 }}>
            <Button variant="outlined" color="primary" onClick={handleMenuOpen}>
              View In...
            </Button>

            <Menu
              anchorEl={menuAnchor}
              id="quick-actions-menu"
              open={menuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {hasInsights && (
                <ExternalLinkMenuItem href={`${INSIGHTS_URL}dashboard?folderIds=${folder.id}`} label="View in Insights" onClick={handleMenuClose} />
              )}
              {hasProtectLite && (
                <ExternalLinkMenuItem href={`${PROTECT_LITE_URL}dashboard/${folder.id}`} label="View in Protect Lite" onClick={handleMenuClose} />
              )}
              <MenuItem
                onClick={() => {
                  setProtectMobileDialog(true)
                  handleMenuClose()
                }}
              >
                View in Protect Mobile
              </MenuItem>
            </Menu>
            <ProtectMobileDialog folderId={folder.id} open={protectMobileDialog} setOpen={setProtectMobileDialog} />

            {showStoreLink && auth.user?.access_token && (
              <Button variant="outlined" color="primary" onClick={() => openSensorStore(folder.id, enqueueSnackbar, auth.user!.access_token, host)}>
                View Sensor Store
              </Button>
            )}
          </Grid>
        </>
      ) : (
        <CircularProgress />
      )}
    </Grid>
  )
}

export default Sidebar
